import { t } from "@/lib/i18n";
import { useEffect, useRef, useState } from "react";
import { HeartHandshake, ArrowUpRight, Send, MapPin, RotateCcw, Sparkles, Loader2, X } from "lucide-react";
import { ChatMessage } from "./ChatMessage";
import { VoiceButton } from "./VoiceButton";
import { ClaraAvatar } from "./Brand";
import { ACTA_CLARA_URL } from "./SeniorSection";


const STEPS = ["ai.step1", "ai.step2", "ai.step3", "ai.step4"];
const SUGGESTIONS = ["sug.0", "sug.1", "sug.2", "sug.3", "sug.4"];

const Header = ({ onReset, onClose, hasMessages }) => (
  <div className="hg-scan relative bg-ac-band px-5 py-4 text-white">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <ClaraAvatar size="h-11 w-11" />
        <div>
          <p className="font-display text-lg font-bold leading-tight">{t("ai.title")}</p>
          <p className="text-[11px] font-medium text-ac-sky">{t("ai.online")}</p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {hasMessages && (
          <button data-testid="ai-reset-button" onClick={onReset} className="flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs text-slate-300 hover:bg-white/10">
            <RotateCcw className="h-3.5 w-3.5" /> {t("ai.new")}
          </button>
        )}
        {onClose && (
          <button data-testid="ai-close-button" onClick={onClose} className="rounded-full p-2 text-slate-300 hover:bg-white/10" aria-label={t("ai.close")}>
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
    <div className="mt-3 grid grid-cols-4 gap-1.5">
      {STEPS.map((s, i) => (
        <span key={s} className="rounded-md border border-white/10 bg-white/5 px-1 py-1 text-center text-[10px] text-slate-300">
          <b className="font-mono text-ac-accent">0{i + 1}</b> {t(s)}
        </span>
      ))}
    </div>
  </div>
);

export const AiAssistant = ({ chat, noAutoFocus, geo, open, draft, setDraft, onClose, onFollowUp }) => {
  const { messages, busy, send, reset } = chat;
  const [useLoc, setUseLoc] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!noAutoFocus && (draft || open)) setTimeout(() => inputRef.current?.focus(), 320);
  }, [draft, open, noAutoFocus]);

  const location = useLoc ? { lat: geo.position[0], lng: geo.position[1], label: geo.place } : null;
  const submit = (text) => {
    send(text ?? draft, location);
    setDraft("");
  };

  return (
    <div data-testid="ai-assistant-panel" className="flex h-full flex-col overflow-hidden bg-ac-bg shadow-[0_30px_70px_-20px_rgba(0,27,62,0.55)] sm:rounded-3xl sm:border sm:border-ac-line">
      <Header onReset={reset} onClose={onClose} hasMessages={messages.length > 0} />
      <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="space-y-2.5">
            <div data-testid="clara-greeting" className="hg-rise flex gap-2.5">
              <ClaraAvatar size="h-8 w-8" online={false} />
              <p className="rounded-2xl rounded-tl-sm border border-ac-line bg-ac-surface px-4 py-3 text-sm leading-relaxed text-ac-text">{t("ai.hello")}</p>
            </div>
            <p className="flex items-center gap-2 text-sm text-ac-muted">
              <Sparkles className="h-4 w-4 text-ac-accent" /> {t("ai.intro")}
            </p>
            {SUGGESTIONS.map((s, i) => (
              <button key={s} data-testid={`ai-suggestion-${i}`} onClick={() => submit(t(s))} className="block w-full rounded-xl border border-ac-line bg-ac-surface px-4 py-2.5 text-left text-sm text-ac-text transition-colors hover:border-ac-primary hover:bg-ac-primary-soft">
                {t(s)}
              </button>
            ))}
            <a data-testid="ai-senior-acta-link" href={ACTA_CLARA_URL} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-between gap-2 rounded-xl border-2 border-ac-primary-line bg-ac-primary-soft px-4 py-3 text-left text-base font-semibold text-ac-primary-ink transition-colors hover:border-ac-primary">
              <span className="flex items-center gap-2"><HeartHandshake className="h-5 w-5 shrink-0" /> {t("senior.chat")}</span>
              <ArrowUpRight className="h-5 w-5 shrink-0" />
            </a>
          </div>
        ) : (
          messages.map((m, i) => (
            <ChatMessage key={i} msg={m} index={i} question={messages[i - 1]?.content || ""} locationLabel={useLoc ? geo.place : null} onFollowUp={onFollowUp} />
          ))
        )}
      </div>
      <div className="border-t border-ac-line bg-ac-surface p-3">
        <button
          data-testid="ai-use-location-toggle"
          onClick={() => setUseLoc(!useLoc)}
          aria-pressed={useLoc}
          className={`mb-2 flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-xs font-medium transition-colors ${
            useLoc ? "border-ac-primary bg-ac-primary-soft text-ac-primary-ink" : "border-ac-line text-ac-muted hover:bg-ac-surface2"
          }`}
        >
          <MapPin className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">{useLoc ? t("ai.usingLoc", { place: geo.place }) : t("ai.useLoc")}</span>
          <span className={`h-4 w-7 shrink-0 rounded-full p-0.5 transition-colors ${useLoc ? "bg-ac-primary" : "bg-ac-line2"}`}>
            <span className={`block h-3 w-3 rounded-full bg-ac-surface transition-transform ${useLoc ? "translate-x-3" : ""}`} />
          </span>
        </button>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            data-testid="ai-assistant-input"
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder={t("ai.ph")}
            className="max-h-32 min-h-[48px] flex-1 resize-none rounded-xl border border-ac-line px-3 py-2.5 text-sm outline-none focus:border-ac-primary focus:ring-4 focus:ring-ac-primary-ring"
          />
          <VoiceButton disabled={busy} onText={(t) => setDraft(draft ? `${draft} ${t}` : t)} />
          <button data-testid="ai-assistant-send-button" type="submit" disabled={busy || !draft.trim()} className="grid h-12 w-12 place-items-center rounded-xl bg-ac-primary text-white transition-colors hover:bg-ac-primary-hover disabled:opacity-40">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </form>
        <p className="mt-1.5 text-[10px] text-ac-faint">{t("ai.disclaimer")}</p>
      </div>
    </div>
  );
};
